import { api } from '../hooks/useApi';
import type {
  Ad,
  ApiEnvelope,
  CreateAdRequest,
  CreateAdResponse,
  ListAdsParams,
  ListAdsResponse,
  AdDetailResponse,
  MyAdsResponse,
  UpdateAdRequest,
  ContactSellerResponse,
  CategorySchemaResponse,
  ImageUploadResponse,
  PresignedUrlResponse,
} from '../types/ad';

/**
 * Returns true when the API envelope represents a successful response.
 * The backend currently uses `success: boolean`; older endpoints may still
 * return `status_code`. We accept either so the service layer keeps working
 * through gradual API migrations.
 */
function isOk<T>(envelope: ApiEnvelope<T>): boolean {
  if (typeof envelope.success === 'boolean') return envelope.success;
  const code = envelope.status_code;
  return code === 200 || code === 201 || code === 204;
}

// ─────────────────────────────────────────────────────────────
// AD LISTING OPERATIONS
// ─────────────────────────────────────────────────────────────

/**
 * List ads with optional filters
 * GET /api/ads/
 */
export const listAds = async (params: ListAdsParams = {}): Promise<ListAdsResponse['data']> => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  const endpoint = `/api/ads/${queryString ? `?${queryString}` : ''}`;

  const response = await api.get<ListAdsResponse>(endpoint);

  if (isOk(response) && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to fetch ads');
};

/**
 * Get ad details by ID
 * GET /api/ads/<uuid>/
 */
export const getAdDetails = async (id: string): Promise<AdDetailResponse['data']> => {
  const response = await api.get<AdDetailResponse>(`/api/ads/${id}/`);

  if (isOk(response) && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to fetch ad details');
};

/**
 * List user's own ads (requires authentication)
 * GET /api/ads/my-ads/
 */
export const getMyAds = async (): Promise<MyAdsResponse['data']> => {
  const response = await api.get<MyAdsResponse>('/api/ads/my-ads/');

  if (isOk(response) && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to fetch your ads');
};

// ─────────────────────────────────────────────────────────────
// AD CRUD OPERATIONS
// ─────────────────────────────────────────────────────────────

/**
 * Create a new ad (requires authentication)
 * POST /api/ads/create/
 *
 * Sends a single multipart/form-data request containing all scalar fields
 * plus any selected images under `image_files`. This matches the backend
 * OpenAPI schema where `image_files` is part of the create payload.
 */
export const createAd = async (
  data: CreateAdRequest,
  images?: File[]
): Promise<CreateAdResponse['data']> => {
  const formData = new FormData();

  // Append scalar fields. Skip undefined/null/empty strings so we don't send
  // empty optional values (some DRF serializers reject empty "" for enums).
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (typeof value === 'boolean') {
      formData.append(key, value ? 'true' : 'false');
    } else if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          formData.append(key, String(item));
        }
      });
    } else if (value instanceof File || value instanceof Blob) {
      formData.append(key, value);
    } else {
      formData.append(key, String(value));
    }
  });

  // Append images as `image_files` (repeated field -> array on the backend).
  if (images && images.length > 0) {
    images.forEach((file) => {
      formData.append('image_files', file, file.name);
    });
  }

  const response = await api.postForm<CreateAdResponse>('/api/ads/create/', formData);

  if (!isOk(response) || !response.data) {
    throw new Error(response.message || 'Failed to create ad');
  }

  return response.data;
};

/**
 * Update an existing ad (requires owner authentication)
 * PUT /api/ads/<uuid>/update/
 */
export const updateAd = async (
  id: string,
  data: UpdateAdRequest
): Promise<Ad> => {
  const response = await api.put<AdDetailResponse>(`/api/ads/${id}/update/`, data);

  if (isOk(response) && response.data?.ad) {
    return response.data.ad;
  }

  throw new Error(response.message || 'Failed to update ad');
};

/**
 * Partial update of an ad (requires owner authentication)
 * PATCH /api/ads/<uuid>/update/
 */
export const patchAd = async (
  id: string,
  data: UpdateAdRequest
): Promise<Ad> => {
  const response = await api.patch<AdDetailResponse>(`/api/ads/${id}/update/`, data);

  if (isOk(response) && response.data?.ad) {
    return response.data.ad;
  }

  throw new Error(response.message || 'Failed to update ad');
};

/**
 * Delete an ad (requires owner or admin authentication)
 * DELETE /api/ads/<uuid>/delete/
 */
export const deleteAd = async (id: string): Promise<void> => {
  const response = await api.delete<ApiEnvelope<null>>(
    `/api/ads/${id}/delete/`
  );

  if (!isOk(response)) {
    throw new Error(response.message || 'Failed to delete ad');
  }
};

/**
 * Mark ad as sold (requires owner authentication)
 * POST /api/ads/<uuid>/sold/
 */
export const markAdAsSold = async (id: string): Promise<Ad> => {
  const response = await api.post<AdDetailResponse>(`/api/ads/${id}/sold/`);

  if (isOk(response) && response.data?.ad) {
    return response.data.ad;
  }

  throw new Error(response.message || 'Failed to mark ad as sold');
};

/**
 * Toggle ad active/inactive (requires owner authentication)
 * POST /api/ads/<uuid>/toggle-active/
 */
export const toggleAdActive = async (id: string): Promise<Ad> => {
  const response = await api.post<AdDetailResponse>(`/api/ads/${id}/toggle-active/`);

  if (isOk(response) && response.data?.ad) {
    return response.data.ad;
  }

  throw new Error(response.message || 'Failed to toggle ad');
};

// ─────────────────────────────────────────────────────────────
// CONTACT SELLER
// ─────────────────────────────────────────────────────────────

/**
 * Get seller contact information (public endpoint)
 * POST /api/ads/<uuid>/contact/
 */
export const contactSeller = async (
  id: string
): Promise<ContactSellerResponse['data']> => {
  const response = await api.post<ContactSellerResponse>(`/api/ads/${id}/contact/`);

  if (isOk(response) && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to get contact information');
};

// ─────────────────────────────────────────────────────────────
// CATEGORY SCHEMA
// ─────────────────────────────────────────────────────────────

/**
 * Get dynamic form fields for a category
 * GET /api/categories/<uuid>/schema/
 */
export const getCategorySchema = async (
  categoryId: string
): Promise<CategorySchemaResponse> => {
  const data = await api.get<CategorySchemaResponse>(
    `/api/categories/${categoryId}/schema/`
  );

  if (isOk(data) && data.data) {
    return data;
  }

  throw new Error(data.message || 'Failed to fetch category schema');
};

// ─────────────────────────────────────────────────────────────
// IMAGE UPLOAD OPERATIONS
// ─────────────────────────────────────────────────────────────

/**
 * Upload images for an ad
 * Uses FormData for multipart upload
 */
export const uploadAdImages = async (
  adId: string,
  images: File[]
): Promise<ImageUploadResponse['data']> => {
  const formData = new FormData();
  formData.append('ad_id', adId);
  
  images.forEach((image, index) => {
    formData.append(`image_${index}`, image);
  });

  const response = await api.postForm<ImageUploadResponse>(
    `/api/ads/${adId}/images/`,
    formData
  );

  if (isOk(response) && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to upload images');
};

/**
 * Get pre-signed URL for direct image upload
 * Use this for large files or when you want direct-to-storage upload
 */
export const getPresignedUploadUrl = async (
  adId: string,
  filename: string,
  contentType: string
): Promise<PresignedUrlResponse['data']> => {
  const response = await api.post<PresignedUrlResponse>(
    `/api/ads/${adId}/images/presigned/`,
    {
      filename,
      content_type: contentType,
    }
  );

  if (isOk(response) && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to get upload URL');
};

/**
 * Delete an image from an ad
 */
export const deleteAdImage = async (
  adId: string,
  imageId: string
): Promise<void> => {
  const response = await api.delete<ApiEnvelope<null>>(
    `/api/ads/${adId}/images/${imageId}/`
  );

  if (!isOk(response)) {
    throw new Error(response.message || 'Failed to delete image');
  }
};

// ─────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────

/**
 * Build filter params from object
 */
export const buildFilterParams = (
  filters: Record<string, unknown>
): ListAdsParams => {
  const params: ListAdsParams = {};
  
  if (filters.category) params.category = String(filters.category);
  if (filters.subcategory) params.subcategory = String(filters.subcategory);
  if (filters.city) params.city = String(filters.city);
  if (filters.minPrice) params.min_price = Number(filters.minPrice);
  if (filters.maxPrice) params.max_price = Number(filters.maxPrice);
  if (filters.condition) params.condition = String(filters.condition);
  if (filters.search) params.search = String(filters.search);
  if (filters.sortBy) params.sort_by = filters.sortBy as ListAdsParams['sort_by'];
  if (filters.page) params.page = Number(filters.page);
  if (filters.limit) params.limit = Number(filters.limit);
  
  return params;
};

/**
 * Format price for display
 */
export const formatPrice = (price: number, currency = 'PKR'): string => {
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(price);
};

/**
 * Format relative time
 */
export const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'Just now';
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  
  const years = Math.floor(days / 365);
  return `${years}y ago`;
};
